import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Layout from './components/Layout';
import AdminLayout from './components/AdminLayout';
import ProtectedRoute from './components/ProtectedRoute';

const HomePage             = lazy(() => import('./pages/HomePage'));
const BrandPage            = lazy(() => import('./pages/BrandPage'));
const ShopPage             = lazy(() => import('./pages/shop/ShopPage'));
const ProductDetailPage    = lazy(() => import('./pages/shop/ProductDetailPage'));
const CartPage             = lazy(() => import('./pages/shop/CartPage'));
const CheckoutPage         = lazy(() => import('./pages/shop/CheckoutPage'));
const OrderCompletePage    = lazy(() => import('./pages/shop/OrderCompletePage'));
const LoginPage            = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage         = lazy(() => import('./pages/auth/RegisterPage'));
const MyPage               = lazy(() => import('./pages/auth/MyPage'));
const MeasureStartPage     = lazy(() => import('./pages/measure/MeasureStartPage'));
const FootTypePage         = lazy(() => import('./pages/measure/FootTypePage'));
const FootTypeIntroPage    = lazy(() => import('./pages/measure/FootTypeIntroPage'));
const CapturePage          = lazy(() => import('./pages/CapturePage'));
const AnalysisPage         = lazy(() => import('./pages/AnalysisPage'));
const ResultPage           = lazy(() => import('./pages/ResultPage'));
const AdminDashboardPage   = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminProductListPage = lazy(() => import('./pages/admin/AdminProductListPage'));
const AdminProductFormPage = lazy(() => import('./pages/admin/AdminProductFormPage'));
const AdminOrderListPage   = lazy(() => import('./pages/admin/AdminOrderListPage'));
const AdminOrderDetailPage = lazy(() => import('./pages/admin/AdminOrderDetailPage'));
const AdminMemberListPage  = lazy(() => import('./pages/admin/AdminMemberListPage'));
const AdminMemberDetailPage = lazy(() => import('./pages/admin/AdminMemberDetailPage'));
const AdminSettingsPage    = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminCategoryPage    = lazy(() => import('./pages/admin/AdminCategoryPage'));
const AdminProductDetailPage = lazy(() => import('./pages/admin/AdminProductDetailPage'));
const PrivacyPage          = lazy(() => import('./pages/PrivacyPage'));
const TermsPage            = lazy(() => import('./pages/TermsPage'));

// HTML 로더 제거 — 레이아웃 종류와 무관하게 한 번만 실행
function useDismissLoader() {
  useEffect(() => {
    const loader = document.getElementById('xv-loader');
    if (!loader) return;
    loader.classList.add('xv-done');
    const id = setTimeout(() => loader.remove(), 800);
    return () => clearTimeout(id);
  }, []);
}

function App() {
  useDismissLoader();
  return (
    <BrowserRouter>
      <LanguageProvider>
      <AuthProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 2000,
            style: { background: '#111827', color: '#fff', fontSize: '14px', borderRadius: '8px' },
          }}
        />
        <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0a0a0a' }} />}>
          <Routes>
            {/* Public pages with Layout */}
            <Route element={<Layout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/brand" element={<BrandPage />} />
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/product/:id" element={<ProductDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/foot-type-intro" element={<FootTypeIntroPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
            </Route>

            {/* Protected pages with Layout */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order/complete" element={<OrderCompletePage />} />
              <Route path="/mypage" element={<MyPage />} />
            </Route>

            {/* Measurement flow (no layout, full screen) */}
            <Route element={<ProtectedRoute />}>
              <Route path="/measure/start" element={<MeasureStartPage />} />
              <Route path="/measure/capture" element={<CapturePage />} />
              <Route path="/measure/analysis" element={<AnalysisPage />} />
              <Route path="/measure/result" element={<ResultPage />} />
              <Route path="/foot-type" element={<FootTypePage />} />
            </Route>

            {/* Admin pages */}
            <Route element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/products" element={<AdminProductListPage />} />
              <Route path="/admin/products/new" element={<AdminProductFormPage />} />
              <Route path="/admin/products/:id" element={<AdminProductFormPage />} />
              <Route path="/admin/orders" element={<AdminOrderListPage />} />
              <Route path="/admin/orders/:id" element={<AdminOrderDetailPage />} />
              <Route path="/admin/members" element={<AdminMemberListPage />} />
              <Route path="/admin/members/:id" element={<AdminMemberDetailPage />} />
              <Route path="/admin/categories" element={<AdminCategoryPage />} />
              <Route path="/admin/detail" element={<AdminProductDetailPage />} />
              <Route path="/admin/settings" element={<AdminSettingsPage />} />
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
